/**
 * Reading the two inputs area pricing is keyed on — when the cleaner arrives,
 * and where — in one place for the server and the browser.
 *
 * The booking app decides which times it prints a saving on; the API decides
 * which tier it charges. If the two parsed "10:00am" or "11215 " differently,
 * the customer would be shown one price and refused at checkout for another,
 * so both import these rather than keeping their own.
 */

export type Arrival = { kind: "fixed"; minutes: number } | { kind: "flexible" };

/**
 * When a booking's cleaner arrives, from a `clean_time` value.
 *
 * Four shapes are live: `flexible`; a fixed time as the funnel writes it
 * (`10:00am`, `2:30 pm`); a bare 24-hour `12:00` from older rows; and a
 * Same-Day window label (`12:00-3:00pm`, `6:00–8:00pm`), read as its start.
 * Anything else is null — a booking we cannot place in the day makes no time
 * claim, rather than a wrong one.
 */
export function parseArrival(value: string | null | undefined): Arrival | null {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "flexible") return { kind: "flexible" };

  const match = text.match(
    /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?(?:\s*[-–]\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)?)?$/
  );
  if (!match) return null;
  const [, h, m = "00", startSuffix, endH, endM = "00", endSuffix] = match;

  const to24 = (hour: number, suffix: string | undefined): number => {
    if (suffix === "am") return hour === 12 ? 0 : hour;
    if (suffix === "pm") return hour === 12 ? 12 : hour + 12;
    return hour;
  };

  const hour = Number(h);
  const minute = Number(m);
  if (hour > 23 || minute > 59) return null;

  let start: number;
  if (startSuffix || !endH) {
    start = to24(hour, startSuffix) * 60 + minute;
  } else {
    /**
     * A window whose start carries no am/pm, like "10:00-12:00pm". The start is
     * the latest reading of its hour that still comes before the end: 10 before
     * noon, 6 before 8pm, 12 before 3pm.
     */
    const end = to24(Number(endH), endSuffix) * 60 + Number(endM);
    const candidates = [hour % 12, (hour % 12) + 12].map((c) => c * 60 + minute);
    const before = candidates.filter((c) => c < end);
    start = before.length ? Math.max(...before) : candidates[0];
  }
  return start >= 0 && start < 24 * 60 ? { kind: "fixed", minutes: start } : null;
}

/**
 * Whatever a caller hands us as a five-digit ZIP, or null.
 *
 * Anything that is not exactly five digits after trimming is not a ZIP, and
 * null is the honest answer — a lookup for "1121" would miss every row and read
 * as "we do not serve there", a different and much worse claim.
 */
export function normalizeZip(value: string | null | undefined): string | null {
  const trimmed = String(value ?? "").trim();
  return /^[0-9]{5}$/.test(trimmed) ? trimmed : null;
}

/** Minutes from midnight, inclusive at both ends. */
export interface MinuteRange {
  start: number;
  end: number;
}

/**
 * The fixed parts of the day that nearby pricing works in: before 11am, 11am to
 * 2pm, and 2pm on. The funnel's own times run 8:00am to 6:30pm, so in practice
 * these are 8–11, 11–2 and 2–6:30.
 *
 * Fixed rather than centred on a neighbour's arrival. A window of "two hours
 * either side of their 11:00am" is 9:00am–1:00pm, and its middle is that
 * customer's arrival time — published to anyone who asks (ADR 0009). A block
 * says only that someone is booked nearby sometime between 11am and 2pm.
 */
export const ARRIVAL_BLOCKS: readonly MinuteRange[] = [
  { start: 0, end: 659 },
  { start: 660, end: 839 },
  { start: 840, end: 1439 },
];

/** The Flexible Time window, 9am–4pm. We choose the arrival inside it. */
export const FLEXIBLE_WINDOW: MinuteRange = { start: 9 * 60, end: 16 * 60 };

/**
 * The blocks an arrival can share a trip in: the one a fixed time falls in, or
 * every block Flexible Time overlaps, since we choose a flexible arrival.
 */
export function blocksFor(arrival: Arrival): MinuteRange[] {
  const span =
    arrival.kind === "flexible"
      ? FLEXIBLE_WINDOW
      : { start: arrival.minutes, end: arrival.minutes };
  return ARRIVAL_BLOCKS.filter((block) => block.start <= span.end && span.start <= block.end);
}
